import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useScrollDetection } from "./useScrollDetection";
import {
  InfiniteScrollConfig,
  InfiniteScrollState,
  UseInfiniteScrollReturn,
  LoadingState,
  DEFAULT_CONFIG,
  PaginationParams,
} from "../types/infiniteScroll";
import { normalize } from "@/utils/utils";

const getItemsFromData = (
  itemsData: { readonly items: any[] } | any[]
): any[] => {
  if (Array.isArray(itemsData)) {
    return itemsData;
  }
  return itemsData.items;
};

// Helper function to determine if search should trigger a request
const shouldTriggerSearch = (searchValue: string | undefined): boolean => {
  if (!searchValue) return true; // Empty string should trigger search (search all)
  return searchValue.length === 0 || searchValue.length >= 3; // 0 or 3+ characters
};

// Helper function to get the effective search query for API calls
const getEffectiveSearchQuery = (
  searchValue: string | undefined
): string | undefined => {
  if (!searchValue || searchValue.length < 3) {
    return undefined; // Don't send query for empty or short searches
  }
  return searchValue;
};

export const useInfiniteScroll = <T, P = any, S = any>(
  config: InfiniteScrollConfig<T, P, S>
): UseInfiniteScrollReturn<T> => {
  const reduxItems = config.useDirectResponse
    ? []
    : (getItemsFromData(config.itemsData) as T[]);

  const mergedConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
      scroll: { ...DEFAULT_CONFIG.scroll, ...config.scroll },
      loading: { ...DEFAULT_CONFIG.loading, ...config.loading },
      retry: { ...DEFAULT_CONFIG.retry, ...config.retry },
    }),
    [config]
  );

  // Internal accumulated items state
  const [accumulatedItems, setAccumulatedItems] = useState<T[]>([]);

  const [state, setState] = useState<InfiniteScrollState>(() => {
    const searchHash = mergedConfig.search?.searchValue!;
    const initialState = {
      currentPage: 1,
      loading: "idle" as const,
      hasMore: true,
      error: null,
      totalLoaded: 0,
      searchHash,
    };
    console.log("[InfiniteScroll] Initial state:", initialState);
    return initialState;
  });

  const apiCallInProgressRef = useRef(false);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const lastItemsLengthRef = useRef(0);
  const searchTimeoutRef = useRef<number | null>(null);
  const lastSearchHashRef = useRef<string | undefined>(undefined);
  const lastParamsHashRef = useRef<string>("");
  const lastApiResponseLength = useRef(0);
  const successfulApiCallsRef = useRef(0);
  const initialLoadCompletedRef = useRef(false);
  const lastRequestedPageRef = useRef(0);

  // Store the latest state values in refs to avoid stale closures
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    lastSearchHashRef.current = mergedConfig.search?.searchValue;
    lastParamsHashRef.current = generateParamsHash(mergedConfig.params);
  }, []);

  const getCurrentPage = useCallback(() => {
    return lastRequestedPageRef.current + 1;
  }, []);

  const generateParamsHash = useCallback((params: any): string => {
    if (!params) return "";
    try {
      return JSON.stringify(params);
    } catch {
      return String(params);
    }
  }, []);

  const log = useCallback(
    (...args: any[]) => {
      if (mergedConfig.debug) {
        console.log("[InfiniteScroll]", ...args);
      }
    },
    [mergedConfig.debug]
  );

  const updateState = useCallback(
    (updates: Partial<InfiniteScrollState>) => {
      if (mountedRef.current) {
        log("Updating state:", updates);
        setState((prev) => {
          const newState = { ...prev, ...updates };
          log("New state:", newState);
          return newState;
        });
      }
    },
    [log]
  );

  const resetRefs = useCallback(() => {
    apiCallInProgressRef.current = false;
    retryCountRef.current = 0;
    lastItemsLengthRef.current = 0;
    lastApiResponseLength.current = 0;
    successfulApiCallsRef.current = 0;
    initialLoadCompletedRef.current = false;
    lastRequestedPageRef.current = 0;
  }, []);

  // Reset accumulated items and refs when search or params change
  const resetAll = useCallback(() => {
    resetRefs();
    setAccumulatedItems([]);
    lastSearchHashRef.current = mergedConfig.search?.searchValue;
    lastParamsHashRef.current = generateParamsHash(mergedConfig.params);
    lastRequestedPageRef.current = 0;
  }, [resetRefs, mergedConfig.search?.searchValue, mergedConfig.params]);

  // Watch for new items from Redux and append them to accumulated items (only if not using direct response)
  useEffect(() => {
    if (config.useDirectResponse) {
      log("Using direct response mode - skipping Redux item updates");
      return;
    }

    if (reduxItems.length > 0) {
      // If this is the first page, replace accumulated items
      if (getCurrentPage() === 1) {
        log("First page - replacing accumulated items");
        setAccumulatedItems(reduxItems);
      } else {
        // Merge items - filter out duplicates and replace with new items
        log(
          "Merging new items with existing, filtering duplicates:",
          reduxItems
        );
        setAccumulatedItems((prev) => {
          const incomingIds = new Set(reduxItems.map((item: any) => item.id));
          const filteredPrev = prev.filter((item: any) => !incomingIds.has(item.id));
          const combined = [...filteredPrev, ...reduxItems];
          log(
            `Merged items: ${prev.length} -> ${filteredPrev.length} (after filtering) + ${reduxItems.length} = ${combined.length}`
          );
          return combined;
        });
      }
    }
  }, [reduxItems, config.useDirectResponse]);

  useEffect(() => {
    if (!mergedConfig.params) return;

    const newParamsHash = generateParamsHash(mergedConfig.params);

    if (
      newParamsHash !== lastParamsHashRef.current &&
      lastParamsHashRef.current !== ""
    ) {
      log("Params changed, resetting pagination");

      resetAll();

      updateState({
        hasMore: true,
        error: null,
        totalLoaded: 0,
        loading: "idle",
      });
    }

    lastParamsHashRef.current = newParamsHash;
  }, [mergedConfig.params, generateParamsHash, log, updateState, resetAll]);

  useEffect(() => {
    const newSearchHash = mergedConfig.search?.searchValue;

    if (
      newSearchHash !== lastSearchHashRef.current &&
      lastSearchHashRef.current !== ""
    ) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Check if the search should trigger a request
      if (!shouldTriggerSearch(newSearchHash)) {
        log(
          `Search value "${newSearchHash}" is too short (1-2 chars), skipping search`
        );
        lastSearchHashRef.current = newSearchHash;
        return;
      }

      const debounceMs = mergedConfig.search?.debounceMs || 300;
      searchTimeoutRef.current = setTimeout(() => {
        if (
          mountedRef.current &&
          mergedConfig.search?.resetOnChange !== false
        ) {
          log("Search changed, resetting pagination");

          resetAll();

          updateState({
            hasMore: true,
            error: null,
            totalLoaded: 0,
            searchHash: newSearchHash,
            loading: "idle",
          });
        }
      }, debounceMs);
    }

    lastSearchHashRef.current = newSearchHash;
  }, [
    mergedConfig.search?.searchValue,
    mergedConfig.search?.debounceMs,
    mergedConfig.search?.resetOnChange,
  ]);

  // Stable loadNextPage function that doesn't depend on state values directly
  const loadNextPage = useCallback(async (): Promise<void> => {
    // Use refs to get current state values to avoid stale closures
    const currentState = stateRef.current;

    if (apiCallInProgressRef.current) {
      log("API call already in progress, skipping");
      return;
    }

    if (!currentState.hasMore) {
      log("No more pages to load (hasMore = false)");
      return;
    }

    if (currentState.loading === "complete") {
      log("Loading already complete, skipping");
      return;
    }

    const currentPage = getCurrentPage();

    if (mergedConfig.maxPages > 0 && currentPage > mergedConfig.maxPages) {
      log("Maximum pages reached");
      updateState({ hasMore: false, loading: "complete" });
      return;
    }

    try {
      apiCallInProgressRef.current = true;
      updateState({ loading: "loading", error: null });

      log(
        `Loading page ${currentPage} (API calls made: ${successfulApiCallsRef.current})`
      );

      // Get the effective search query (undefined for short searches)
      const effectiveQuery = getEffectiveSearchQuery(
        mergedConfig.search?.searchValue
      );

      const paginationParams: PaginationParams = {
        page_number: currentPage,
        page_size: mergedConfig.initialPageSize,
        query: effectiveQuery ? normalize(effectiveQuery) : undefined,
      };

      const apiParams = {
        ...mergedConfig.apiParams,
        ...mergedConfig.params,
        ...paginationParams,
      };

      // Remove undefined query from params to clean up the request
      if (apiParams.query === undefined) {
        delete apiParams.query;
      }

      const result = await mergedConfig.apiAction(apiParams);

      if (!mountedRef.current) {
        log("Component unmounted, exiting");
        return;
      }

      log("API call successful:", result);

      lastRequestedPageRef.current = currentPage;
      successfulApiCallsRef.current++;
      initialLoadCompletedRef.current = true;
      retryCountRef.current = 0;

      let responseLength = 0;
      let responseItems: T[] = [];

      if (result.payload.items && Array.isArray(result.payload.items)) {
        responseLength = result.payload.items.length;
        responseItems = result.payload.items;
      } else if (result.payload && Array.isArray(result.payload)) {
        responseLength = result.payload.length;
        responseItems = result.payload;
      }

      lastApiResponseLength.current = responseLength;

      log(
        `Response length: ${responseLength}, Page size: ${mergedConfig.initialPageSize}`
      );

      // Handle direct response mode
      if (config.useDirectResponse) {
        log("Direct response mode: updating accumulated items directly");
        setAccumulatedItems((prev) => {
          if (currentPage === 1) {
            // First page - replace all items
            return responseItems;
          } else {
            // Subsequent pages - append items
            return [...prev, ...responseItems];
          }
        });
      }

      const emptyResponse = responseLength === 0;
      const partialPage = responseLength < mergedConfig.initialPageSize;
      const shouldStop = emptyResponse || partialPage;

      // Update state immediately to prevent race condition with intersection observer
      if (shouldStop) {
        log(
          `Pagination complete. Empty response: ${emptyResponse}, Partial page: ${partialPage}`
        );
        updateState({
          loading: "complete",
          hasMore: false,
          totalLoaded: config.useDirectResponse
            ? currentPage === 1
              ? responseLength
              : accumulatedItems.length + responseLength
            : accumulatedItems.length + responseLength,
        });
      } else {
        log("More pages available, continuing pagination");
        updateState({
          loading: "idle",
          hasMore: true,
        });
      }

      // For direct response mode, update totalLoaded immediately
      if (config.useDirectResponse) {
        const newTotalLoaded =
          currentPage === 1
            ? responseLength
            : accumulatedItems.length + responseLength;
        updateState({
          totalLoaded: newTotalLoaded,
        });
      } else {
        // Wait for the items to be updated and check multiple times if needed (Redux mode)
        const checkItemsUpdate = (attemptCount = 0) => {
          if (!mountedRef.current) return;

          // Check if accumulated items have been updated
          const currentAccumulatedLength = accumulatedItems.length;
          const expectedLength = lastItemsLengthRef.current + responseLength;

          log(
            `Checking items update: currentAccumulated=${currentAccumulatedLength}, expected=${expectedLength}, attempt=${attemptCount}`
          );

          // If items haven't updated yet and we haven't tried too many times, wait and try again
          if (currentAccumulatedLength < expectedLength && attemptCount < 10) {
            setTimeout(() => checkItemsUpdate(attemptCount + 1), 100);
            return;
          }

          lastItemsLengthRef.current = currentAccumulatedLength;

          // Only update totalLoaded here, hasMore and loading are already set above
          updateState({
            totalLoaded: currentAccumulatedLength,
          });
        };

        // Start checking for items update
        checkItemsUpdate();
      }
    } catch (error: any) {
      if (!mountedRef.current) return;

      log("API call failed:", error);

      const errorMessage = error?.message || "Failed to load data";

      if (
        mergedConfig.retry.enabled &&
        mergedConfig.retry.maxAttempts &&
        retryCountRef.current < mergedConfig.retry.maxAttempts
      ) {
        retryCountRef.current++;

        log(
          `Retrying in ${mergedConfig.retry.delay}ms (attempt ${retryCountRef.current}/${mergedConfig.retry.maxAttempts})`
        );

        setTimeout(() => {
          if (mountedRef.current) {
            loadNextPage();
          }
        }, mergedConfig.retry.delay);

        return;
      }

      updateState({
        loading: "error",
        error: errorMessage,
      });

      retryCountRef.current = 0;
    } finally {
      apiCallInProgressRef.current = false;
      log(
        `🏁 loadNextPage finished, apiCallInProgress: ${apiCallInProgressRef.current}`
      );
    }
  }, [
    getCurrentPage,
    mergedConfig.apiAction,
    mergedConfig.apiParams,
    mergedConfig.params,
    mergedConfig.initialPageSize,
    mergedConfig.maxPages,
    mergedConfig.retry,
    mergedConfig.search?.searchValue,
    accumulatedItems.length,
    config.useDirectResponse,
  ]);

  const reset = useCallback(() => {
    log("Resetting infinite scroll");

    // Stop any in-progress API calls by marking as unmounted temporarily
    const wasInProgress = apiCallInProgressRef.current;
    apiCallInProgressRef.current = false;

    resetAll();

    // Clear any pending timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    updateState({
      loading: "idle",
      hasMore: true,
      error: null,
      totalLoaded: 0,
      searchHash: mergedConfig.search?.searchValue,
    });

    log(`Reset complete, was in progress: ${wasInProgress}`);
  }, [mergedConfig.search?.searchValue, resetAll, log, updateState]);

  const refresh = useCallback(async () => {
    log("Refreshing data");
    reset();

    setTimeout(() => {
      if (mountedRef.current) {
        loadNextPage();
      }
    }, 0);
  }, [reset, loadNextPage, log]);

  const retry = useCallback(() => {
    if (state.loading === "error") {
      log("Manual retry triggered");
      retryCountRef.current = 0;
      loadNextPage();
    }
  }, [state.loading, loadNextPage, log]);

  // Stable scroll threshold handler that doesn't recreate frequently
  const handleScrollThreshold = useCallback(() => {
    const currentState = stateRef.current;

    console.log("[InfiniteScroll] handleScrollThreshold called with state:", {
      loading: currentState.loading,
      hasMore: currentState.hasMore,
      error: currentState.error,
      apiInProgress: apiCallInProgressRef.current,
    });

    if (
      currentState.loading === "loading" ||
      !currentState.hasMore ||
      currentState.loading === "error" ||
      currentState.loading === "complete"
    ) {
      log("Scroll threshold reached but conditions not met:", {
        loading: currentState.loading,
        hasMore: currentState.hasMore,
      });
      return;
    }

    if (apiCallInProgressRef.current) {
      log("API call already in progress, skipping scroll threshold");
      return;
    }

    log("✅ Scroll threshold reached - loading next page");
    loadNextPage();
  }, [loadNextPage, log]);

  // Determine if scroll detection should be enabled - using a more explicit check
  const scrollDetectionEnabled = useMemo(() => {
    const enabled =
      state.hasMore &&
      state.loading === "idle" &&
      !apiCallInProgressRef.current &&
      state.error === null &&
      mountedRef.current;

    log(
      `📡 Scroll detection enabled: ${enabled} (hasMore: ${state.hasMore}, loading: ${state.loading}, apiInProgress: ${apiCallInProgressRef.current}, error: ${state.error})`
    );
    return enabled;
  }, [state.hasMore, state.loading, state.error, log]);

  const scrollDetection = useScrollDetection({
    ...mergedConfig.scroll,
    onReachThreshold: handleScrollThreshold,
    enabled: scrollDetectionEnabled,
  });

  // Debug scroll detection creation
  useEffect(() => {
    log("🔧 Scroll detection instance created/updated:", {
      enabled: scrollDetectionEnabled,
      threshold: mergedConfig.scroll.threshold,
      container: mergedConfig.scroll.scrollContainer,
      hasAttachSentinel: !!scrollDetection?.attachSentinel,
    });
  }, [scrollDetection, scrollDetectionEnabled, mergedConfig.scroll, log]);

  // Auto-load first page logic
  useEffect(() => {
    if (
      mergedConfig.autoLoadFirst &&
      state.loading === "idle" &&
      !initialLoadCompletedRef.current &&
      successfulApiCallsRef.current === 0 &&
      !apiCallInProgressRef.current
    ) {
      log("Auto-loading first page");
      loadNextPage();
    }
  }, [mergedConfig.autoLoadFirst, state.loading, loadNextPage, log]);

  // Update totalLoaded when accumulated items change (only for Redux mode)
  useEffect(() => {
    if (
      !config.useDirectResponse &&
      accumulatedItems.length !== state.totalLoaded &&
      state.loading !== "loading"
    ) {
      log(
        `Accumulated items length changed: ${state.totalLoaded} -> ${accumulatedItems.length}`
      );
      updateState({ totalLoaded: accumulatedItems.length });
    }
  }, [
    accumulatedItems.length,
    state.totalLoaded,
    state.loading,
    updateState,
    log,
    config.useDirectResponse,
  ]);

  // Set mounted to true on mount, false on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    log("Component mounted or key changed - performing complete reset");

    setAccumulatedItems([]);

    // Reset state to initial values
    const searchHash = mergedConfig.search?.searchValue;
    setState({
      currentPage: 1,
      loading: "idle",
      hasMore: true,
      error: null,
      totalLoaded: 0,
      searchHash,
    });

    // Clear any pending timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Update hash refs
    lastSearchHashRef.current = searchHash;
    lastParamsHashRef.current = generateParamsHash(mergedConfig.params);

    log("Complete reset finished");
  }, []); // Empty dependency array - runs only on mount

  const isLoading = state.loading === "loading";
  const hasMore =
    state.hasMore && state.loading !== "error" && state.loading !== "complete";
  const currentPage = getCurrentPage();

  return {
    items: accumulatedItems,
    state: {
      ...state,
      currentPage,
    },
    loadMore: loadNextPage,
    reset,
    refresh,
    retry,
    isLoading,
    hasMore,
    error: state.error,
    scrollDetection,
  };
};
